/**
 * PdfConversionPanel
 * 
 * Panel for converting PDF documents to Word format.
 * Shows when user uploads PDF and asks to convert to Word.
 * Supports Hebrew (RTL) and English (LTR) interfaces.
 */

import React, { useState, useCallback, useMemo } from 'react';
import {
    Stack,
    Text,
    PrimaryButton,
    DefaultButton,
    ProgressIndicator,
    MessageBar,
    MessageBarType,
    Icon,
    Spinner,
    SpinnerSize
} from '@fluentui/react';
import { authenticatedFetch } from '../../utils/authApi';
import styles from './DocumentTranslationPanel.module.css';

// Bilingual text support
const TEXT = {
    en: {
        panelTitle: 'PDF to Word Conversion',
        cancel: 'Cancel',
        convertButton: 'Convert to Word',
        converting: 'Converting...',
        convertingMessage: 'Converting PDF to Word... This may take a moment.',
        conversionComplete: 'Conversion Complete!',
        downloadWord: 'Download Word Document',
        download: 'Download',
        minimize: 'Minimize',
        close: 'Close',
        tryAgain: 'Try Again',
        wordReady: 'Word document ready:',
        convertDescription: 'Convert this PDF to an editable Word document. Formatting, tables, and images will be preserved.',
        noFiles: 'No PDF files to convert',
        fileNotAvailable: 'File not available for conversion',
        conversionFailed: 'Conversion failed',
        errorOccurred: 'An error occurred during conversion',
        page: 'page',
        pages: 'pages',
        footer: 'Powered by pdf2docx • Preserves formatting, tables, and images'
    },
    he: {
        panelTitle: 'המרת PDF ל-Word',
        cancel: 'ביטול',
        convertButton: 'המר ל-Word',
        converting: 'ממיר...',
        convertingMessage: 'ממיר PDF ל-Word... זה עשוי לקחת רגע.',
        conversionComplete: 'ההמרה הושלמה!',
        downloadWord: 'הורד מסמך Word',
        download: 'הורדה',
        minimize: 'מזער',
        close: 'סגור',
        tryAgain: 'נסה שוב',
        wordReady: 'מסמך Word מוכן:',
        convertDescription: 'המר PDF זה למסמך Word ניתן לעריכה. עיצוב, טבלאות ותמונות יישמרו.',
        noFiles: 'אין קבצי PDF להמרה',
        fileNotAvailable: 'הקובץ לא זמין להמרה',
        conversionFailed: 'ההמרה נכשלה',
        errorOccurred: 'אירעה שגיאה במהלך ההמרה',
        page: 'עמוד',
        pages: 'עמודים',
        footer: 'מופעל על ידי pdf2docx • שומר על עיצוב, טבלאות ותמונות'
    }
};

// Detect if Hebrew should be used
const detectLanguage = (): 'he' | 'en' => {
    // Check document direction
    const dir = document.documentElement.dir || document.body.dir;
    if (dir === 'rtl') return 'he';
    
    // Check browser language
    const lang = navigator.language || (navigator as any).userLanguage || '';
    if (lang.startsWith('he')) return 'he';
    
    return 'en';
};

interface UploadedFile {
    id: string;
    name?: string;
    filename?: string;
    file?: File;
    type?: string;
    size?: number;
}

interface ConversionResult {
    success: boolean;
    word_document_url?: string;
    word_filename?: string;
    page_count?: number;
    error?: string;
}

interface PdfConversionPanelProps {
    uploadedFiles: UploadedFile[];
    onConversionComplete?: (result: ConversionResult) => void;
    onDismiss?: () => void;
}

type ConversionStatus = 'idle' | 'converting' | 'completed' | 'error';

export const PdfConversionPanel: React.FC<PdfConversionPanelProps> = ({
    uploadedFiles,
    onConversionComplete,
    onDismiss
}) => {
    const [status, setStatus] = useState<ConversionStatus>('idle');
    const [progress, setProgress] = useState<number>(0);
    const [result, setResult] = useState<ConversionResult | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [isMinimized, setIsMinimized] = useState<boolean>(false);
    
    // Get localized text
    const t = useMemo(() => TEXT[detectLanguage()], []);
    const isRtl = useMemo(() => detectLanguage() === 'he', []);

    // Get PDF files only
    const pdfFiles = uploadedFiles.filter(f => {
        const name = f.filename || f.name || '';
        return name.toLowerCase().endsWith('.pdf');
    });

    const getFileName = (file: UploadedFile): string => {
        return file.filename || file.name || 'Unknown file';
    };

    const handleConvert = useCallback(async () => {
        if (pdfFiles.length === 0) {
            setError('No PDF files to convert');
            return;
        }

        setStatus('converting');
        setProgress(20);
        setError(null);

        try {
            const file = pdfFiles[0]; // Convert first PDF
            const formData = new FormData();
            
            if (file.file) {
                // Direct file upload
                formData.append('file', file.file);
            } else {
                // Need to use URL-based conversion
                setError('File not available for conversion');
                setStatus('error');
                return;
            }

            setProgress(50);

            const response = await authenticatedFetch('/api/translation/convert-to-word', {
                method: 'POST',
                body: formData
            });

            const data = await response.json();
            setProgress(100);

            if (data.success) {
                setStatus('completed');
                setResult(data);
                onConversionComplete?.(data);
            } else {
                throw new Error(data.error || 'Conversion failed');
            }
        } catch (err: any) {
            setStatus('error');
            setError(err.message || 'An error occurred during conversion');
            console.error('[PdfConversion] Error:', err);
        }
    }, [pdfFiles, onConversionComplete]);

    const handleDownload = useCallback(() => {
        if (result?.word_document_url) {
            const link = document.createElement('a');
            link.href = result.word_document_url;
            link.download = result.word_filename || 'converted.docx';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        }
    }, [result]);

    const handleMinimize = useCallback(() => {
        setIsMinimized(true);
    }, []);

    const handleRestore = useCallback(() => {
        setIsMinimized(false);
    }, []);

    // Minimized view
    if (isMinimized && status === 'completed' && result) {
        return (
            <div 
                onClick={handleRestore}
                dir={isRtl ? 'rtl' : 'ltr'}
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '10px 16px',
                    backgroundColor: '#2b579a',
                    color: 'white',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    marginBottom: '8px'
                }}
            >
                <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 8 }}>
                    <Icon iconName="WordDocument" />
                    <Text style={{ color: 'white', fontWeight: 500 }}>
                        {t.wordReady} {result.word_filename}
                    </Text>
                </Stack>
                <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 8 }}>
                    <DefaultButton
                        text={t.download}
                        iconProps={{ iconName: 'Download' }}
                        onClick={(e) => { e.stopPropagation(); handleDownload(); }}
                        styles={{ root: { backgroundColor: 'white', color: '#2b579a' } }}
                    />
                    <Icon 
                        iconName="Cancel" 
                        onClick={(e) => { e.stopPropagation(); onDismiss?.(); }}
                        style={{ cursor: 'pointer', opacity: 0.8 }}
                        title={t.close}
                    />
                </Stack>
            </div>
        );
    }

    return (
        <div className={styles.panelContainer} dir={isRtl ? 'rtl' : 'ltr'} style={{ background: 'linear-gradient(135deg, #f3f6fb 0%, #e8eef8 100%)', borderColor: '#2b579a' }}>
            <div className={styles.panelHeader} style={{ background: 'linear-gradient(90deg, #2b579a 0%, #3a6eb5 100%)' }}>
                <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 8 }}>
                    {status === 'converting' ? (
                        <Spinner size={SpinnerSize.small} />
                    ) : status === 'completed' ? (
                        <Icon iconName="CheckMark" className={styles.successIcon} />
                    ) : status === 'error' ? (
                        <Icon iconName="ErrorBadge" className={styles.errorIcon} />
                    ) : (
                        <Icon iconName="WordDocument" style={{ color: 'white', fontSize: '18px' }} />
                    )}
                    <Text variant="mediumPlus" className={styles.headerTitle}>
                        {t.panelTitle}
                    </Text>
                </Stack>
                {onDismiss && status === 'idle' && (
                    <DefaultButton
                        iconProps={{ iconName: 'Cancel' }}
                        onClick={onDismiss}
                        className={styles.dismissButton}
                        text={t.cancel}
                    />
                )}
            </div>

            <div className={styles.panelContent}>
                {/* Error message */}
                {error && (
                    <MessageBar
                        messageBarType={MessageBarType.error}
                        onDismiss={() => setError(null)}
                        className={styles.messageBar}
                    >
                        {error}
                    </MessageBar>
                )}

                {/* Status messages */}
                {status === 'idle' && pdfFiles.length > 0 && (
                    <>
                        <div className={styles.selectedFile}>
                            <Icon iconName="PDF" style={{ color: '#d32f2f', fontSize: '24px' }} />
                            <Text className={styles.fileName}>{getFileName(pdfFiles[0])}</Text>
                        </div>
                        <Text style={{ color: '#666', fontSize: '13px', marginBottom: '12px' }}>
                            {t.convertDescription}
                        </Text>
                    </>
                )}

                {status === 'converting' && (
                    <>
                        <Text style={{ color: '#444', marginBottom: '12px' }}>
                            {t.convertingMessage}
                        </Text>
                        <ProgressIndicator
                            label={t.converting}
                            percentComplete={progress / 100}
                            className={styles.progressIndicator}
                        />
                    </>
                )}

                {status === 'completed' && result && (
                    <div className={styles.resultContainer}>
                        <div className={styles.successHeader}>
                            <Icon iconName="CheckMark" className={styles.successCheckIcon} />
                            <Text className={styles.successTitle}>
                                {t.conversionComplete}
                            </Text>
                        </div>
                        
                        <div className={styles.documentReadyCard}>
                            <div className={styles.documentIconWrapper}>
                                <Icon iconName="WordDocument" style={{ color: '#2b579a', fontSize: '24px' }} />
                            </div>
                            <div className={styles.documentInfo}>
                                <Text className={styles.documentName}>
                                    {result.word_filename}
                                </Text>
                                {result.page_count && (
                                    <Text style={{ fontSize: '12px', color: '#666' }}>
                                        {result.page_count} {result.page_count > 1 ? t.pages : t.page}
                                    </Text>
                                )}
                            </div>
                        </div>

                        <PrimaryButton
                            iconProps={{ iconName: 'Download' }}
                            className={styles.downloadButton}
                            onClick={handleDownload}
                            style={{ backgroundColor: '#2b579a', borderColor: '#2b579a' }}
                        >
                            {t.downloadWord}
                        </PrimaryButton>
                        
                        {onDismiss && (
                            <Stack horizontal tokens={{ childrenGap: 8 }} style={{ marginTop: '12px' }}>
                                <DefaultButton
                                    text={t.minimize}
                                    iconProps={{ iconName: 'ChevronDown' }}
                                    onClick={handleMinimize}
                                />
                                <DefaultButton
                                    text={t.close}
                                    iconProps={{ iconName: 'Cancel' }}
                                    onClick={onDismiss}
                                />
                            </Stack>
                        )}
                    </div>
                )}

                {/* Action button */}
                {status === 'idle' && pdfFiles.length > 0 && (
                    <PrimaryButton
                        iconProps={{ iconName: 'Sync' }}
                        text={t.convertButton}
                        onClick={handleConvert}
                        style={{ backgroundColor: '#2b579a', borderColor: '#2b579a' }}
                    />
                )}

                {/* Error retry */}
                {status === 'error' && (
                    <Stack horizontal tokens={{ childrenGap: 8 }}>
                        <PrimaryButton
                            iconProps={{ iconName: 'Refresh' }}
                            text={t.tryAgain}
                            onClick={() => {
                                setStatus('idle');
                                setError(null);
                            }}
                        />
                        {onDismiss && (
                            <DefaultButton
                                text={t.cancel}
                                onClick={onDismiss}
                            />
                        )}
                    </Stack>
                )}
            </div>

            <div className={styles.panelFooter}>
                <Text variant="xSmall" className={styles.footerText}>
                    {t.footer}
                </Text>
            </div>
        </div>
    );
};

export default PdfConversionPanel;
